import apiClient from './client';
import { API_ROUTES } from './routes';

// ========== Types ==========

export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country?: string;
}

export interface CreateCardholderRequest {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  billingAddress: BillingAddress;
  phoneNumber?: string;
  termsAccepted: boolean;
}

export interface Cardholder {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  status: 'active' | 'inactive' | 'blocked';
  type: 'individual' | 'company';
  createdAt: string;
}

export interface SpendingLimit {
  amount: number;
  interval: 'per_authorization' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all_time';
}

export interface SpendingControls {
  spendingLimits: SpendingLimit[];
  allowedCategories?: string[] | null;
  blockedCategories?: string[] | null;
}

export interface CreateCardRequest {
  currency: 'usd' | 'gbp' | 'eur';
  spendingControls?: {
    spendingLimits?: SpendingLimit[];
    allowedCategories?: string[];
    blockedCategories?: string[];
  };
}

export interface Card {
  id: string;
  cardholderId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  currency: string;
  status: 'active' | 'inactive' | 'canceled';
  type: 'virtual' | 'physical';
  spendingControls: SpendingControls;
  createdAt: string;
}

export interface CardDetails extends Card {
  number: string;
  cvc: string;
}

export interface UpdateCardStatusRequest {
  status: 'active' | 'inactive';
}

export interface UpdateSpendingControlsRequest {
  spendingLimits?: SpendingLimit[];
  allowedCategories?: string[];
  blockedCategories?: string[];
}

export interface Transaction {
  id: string;
  amount: number;
  currency: string;
  merchantName?: string | null;
  merchantCategory?: string | null;
  merchantCity?: string | null;
  merchantCountry?: string | null;
  type: 'capture' | 'refund';
  createdAt: string;
}

export interface TransactionsListResponse {
  transactions: Transaction[];
  hasMore: boolean;
}

export interface IssuingBalance {
  available: number;
  pending: number;
  currency: string;
}

// ========== API ==========

export const cardsApi = {
  // Cardholder endpoints
  async createCardholder(data: CreateCardholderRequest): Promise<Cardholder> {
    const response = await apiClient.post<Cardholder>(API_ROUTES.CARDS.CARDHOLDERS, data);
    return response.data;
  },

  async getCardholder(): Promise<Cardholder | null> {
    const response = await apiClient.get<Cardholder | null>(API_ROUTES.CARDS.CARDHOLDER_ME);
    return response.data;
  },

  // Card endpoints
  async createCard(data: CreateCardRequest): Promise<Card> {
    const response = await apiClient.post<Card>(API_ROUTES.CARDS.CREATE, data);
    return response.data;
  },

  async getCards(): Promise<Card[]> {
    const response = await apiClient.get<Card[]>(API_ROUTES.CARDS.LIST);
    return response.data;
  },

  async getCard(cardId: string): Promise<Card | null> {
    const response = await apiClient.get<Card | null>(API_ROUTES.CARDS.BY_ID(cardId));
    return response.data;
  },

  async getCardDetails(cardId: string): Promise<CardDetails> {
    const response = await apiClient.get<CardDetails>(API_ROUTES.CARDS.DETAILS(cardId));
    return response.data;
  },

  async updateCardStatus(cardId: string, data: UpdateCardStatusRequest): Promise<Card> {
    const response = await apiClient.patch<Card>(API_ROUTES.CARDS.STATUS(cardId), data);
    return response.data;
  },

  async updateSpendingControls(cardId: string, data: UpdateSpendingControlsRequest): Promise<Card> {
    const response = await apiClient.patch<Card>(API_ROUTES.CARDS.SPENDING_CONTROLS(cardId), data);
    return response.data;
  },

  // Transaction endpoints
  async getTransactions(
    cardId: string,
    limit: number = 20,
    startingAfter?: string,
  ): Promise<TransactionsListResponse> {
    const params: Record<string, string | number> = { limit };
    if (startingAfter) {
      params.starting_after = startingAfter;
    }
    const response = await apiClient.get<TransactionsListResponse>(
      API_ROUTES.CARDS.TRANSACTIONS(cardId),
      {
        params,
      },
    );
    return response.data;
  },

  // Balance endpoints
  async getIssuingBalance(): Promise<IssuingBalance> {
    const response = await apiClient.get<IssuingBalance>(API_ROUTES.CARDS.BALANCE_ISSUING);
    return response.data;
  },
};
